import { FC } from "react"
import Checkbox from "@/components/Checkbox"
import { SANDMAN_PROVIDER_OPTIONS } from "@/utils/sandman"
import type { SandmanConfig, SandmanProvider } from "@/types/entities/campaign"

type Props = {
  value: SandmanConfig
  disabled?: boolean
  description?: string
  testIdPrefix: string
  onChange: (value: SandmanConfig) => void
}

const SandmanSettingsControls: FC<Props> = ({
  value,
  disabled = false,
  description,
  testIdPrefix,
  onChange,
}) => {
  const handleEnabledChange = (checked: boolean) => {
    onChange({
      ...value,
      enabled: checked,
    })
  }

  const handleProviderChange = (
    provider: SandmanProvider,
    field: "run" | "gate",
    checked: boolean,
  ) => {
    onChange({
      ...value,
      providers: {
        ...value.providers,
        [provider]: {
          ...value.providers[provider],
          [field]: checked,
          ...(field === "run" && !checked ? { gate: false } : {}),
        },
      },
    })
  }

  return (
    <div data-testid={`${testIdPrefix}-settings`}>
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-3">
          <Checkbox
            id={`${testIdPrefix}-enabled`}
            className="checkbox-primary h-[18px] w-[18px]"
            checked={value.enabled}
            disabled={disabled}
            onChange={({ target: { checked } }) => handleEnabledChange(checked)}
            data-testid={`${testIdPrefix}-enabled`}
          />
          <label
            className="cursor-pointer text-sm font-medium leading-6"
            htmlFor={`${testIdPrefix}-enabled`}
          >
            Enable Sandman
          </label>
        </div>
        {description && (
          <p className="text-sm leading-6 text-text-light">{description}</p>
        )}
      </div>

      <div className="mt-4 overflow-x-auto">
        <div className="min-w-[520px] overflow-hidden rounded-md border border-outline">
          <div className="grid grid-cols-[1fr_140px_160px] bg-bg-default px-4 py-2 text-xs font-medium uppercase text-text-light">
            <span>Provider</span>
            <span>Run Sandman</span>
            <span>Require pass</span>
          </div>
          {SANDMAN_PROVIDER_OPTIONS.map(({ key, label }) => {
            const provider = value.providers[key]
            const providerDisabled = disabled || !value.enabled
            const gateDisabled = providerDisabled || !provider.run

            return (
              <div
                key={key}
                className="grid grid-cols-[1fr_140px_160px] items-center border-t border-outline px-4 py-3 text-sm"
              >
                <span className="font-medium">{label}</span>
                <div className="flex items-center">
                  <Checkbox
                    id={`${testIdPrefix}-${key}-run`}
                    className="checkbox-primary h-[18px] w-[18px]"
                    checked={provider.run}
                    disabled={providerDisabled}
                    onChange={({ target: { checked } }) =>
                      handleProviderChange(key, "run", checked)
                    }
                    data-testid={`${testIdPrefix}-${key}-run`}
                  />
                </div>
                <div className="flex items-center">
                  <Checkbox
                    id={`${testIdPrefix}-${key}-gate`}
                    className="checkbox-primary h-[18px] w-[18px]"
                    checked={provider.gate}
                    disabled={gateDisabled}
                    onChange={({ target: { checked } }) =>
                      handleProviderChange(key, "gate", checked)
                    }
                    data-testid={`${testIdPrefix}-${key}-gate`}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default SandmanSettingsControls
